import { Lesson } from '@/app/entities/Training'
import { useLessonMutations } from '@/app/hooks/useLessonMutations'
import { cn } from '@/app/utils/cn'
import { Button } from '@/view/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/view/components/ui/form'
import { Input } from '@/view/components/ui/input'
import { Label } from '@/view/components/ui/label'
import { Switch } from '@/view/components/ui/switch'
import { Textarea } from '@/view/components/ui/textarea'
import { memo, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LessonFormValues, useClassForm } from '../../hooks/useClssForm'
import { FileDropzone } from './FilesDropzone'
import { ThumnailDropzone } from './ThumbnailDropzone'
import { VideoDropzone } from './VideoDropzone'
import { parseLessonToFormValues } from './helpers'
import YouTubeDurationFetcher from '@/view/components/YoutubeDurationFetcher'

interface ClassFormProps {
  lesson?: Lesson
  trainingId: string
}

// Memoizing the VideoDropzone component to prevent unnecessary re-renders changing another values
const MemoizedVideoDropzone = memo(VideoDropzone)

export const LessonForm = ({ lesson, trainingId }: ClassFormProps) => {
  const isEdit = !!lesson
  const [lessonData, setLessonData] = useState<LessonFormValues | undefined>(
    undefined,
  )

  const navigate = useNavigate()

  const handleParsedLesson = useCallback(async () => {
    if (!lesson) return
    const parsedLesson = await parseLessonToFormValues(lesson)
    setLessonData(parsedLesson)
  }, [lesson])

  const form = useClassForm({
    getDefaultValue: lesson ? lessonData : undefined,
  })

  const isEditing = !!lesson

  const {
    createLessonMutation,
    updateLessonMutation,
    deleteAttachmentMutation,
  } = useLessonMutations()

  useEffect(() => {
    handleParsedLesson()
  }, [handleParsedLesson])

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isEditing) {
        await updateLessonMutation.mutateAsync({
          lessonId: lesson?._id,
          lesson: data,
        })
      } else {
        await createLessonMutation.mutateAsync({ trainingId, lesson: data })
      }

      navigate(-1)
    } catch (error) {
      console.error(error)
    }
  })

  const description = form.watch('description')
  const isYoutubeVideo = form.watch('isYoutubeVideo')

  const isMutating =
    createLessonMutation.isPending || updateLessonMutation.isPending

  const isDeletingFile = deleteAttachmentMutation.isPending

  const handleCancel = () => {
    navigate(-1)
  }

  const handleDeleteAttachment = async (
    _: string,
    attachmentId: string | undefined,
  ) => {
    if (!isEdit || !attachmentId) return
    await deleteAttachmentMutation.mutateAsync(attachmentId)
  }

  const videoUrl = form.watch('videoUrl')

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="mt-8 flex w-full gap-8">
        <div className="w-2/3 space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome da aula</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Aula Exemplo"
                    {...field}
                    value={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Explique sua aula"
                    {...field}
                    value={field.value}
                  />
                </FormControl>
                <FormMessage />
                <div className="flex justify-end">
                  <span className="text-sm text-muted-foreground">
                    {description?.length}/500
                  </span>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="thumbnailFile"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Miniatura</FormLabel>
                <FormControl>
                  <ThumnailDropzone
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex w-full justify-end gap-4">
            <Button
              className="rounded-full bg-gray-100 text-black03 hover:bg-gray-200"
              onClick={handleCancel}
            >
              Cancelar
            </Button>
            <Button
              className="rounded-full text-white"
              disabled={isMutating}
              type="submit"
            >
              {lesson ? 'Salvar alterações' : 'Criar aula'}
            </Button>
          </div>
        </div>
        <div className="w-1/3 space-y-4">
          {isYoutubeVideo && (
            <FormField
              control={form.control}
              name="videoUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vídeo</FormLabel>
                  <FormControl>
                    <Input placeholder="Youtube URL" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {isYoutubeVideo && videoUrl && (
            <YouTubeDurationFetcher
              url={videoUrl}
              onDuration={(duration) => {
                form.setValue('duration', duration)
              }}
            />
          )}

          {!isYoutubeVideo && (
            <FormField
              control={form.control}
              name="videoFile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vídeo</FormLabel>
                  <FormControl>
                    <MemoizedVideoDropzone
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="isYoutubeVideo"
            render={({ field }) => (
              <FormItem className="w-full space-y-0">
                <FormControl
                  className={cn('w-full rounded-md bg-primary/10', {
                    'bg-gray-100': !field.value,
                  })}
                >
                  <div className="flex items-center justify-center space-x-2 p-3">
                    <Label htmlFor="yt" className="font-semibold">
                      Utilizar vídeo do Youtube
                    </Label>
                    <Switch
                      id="yt"
                      checked={field.value}
                      onCheckedChange={(value) => {
                        field.onChange(value)
                        if (value) {
                          form.setValue('videoFile', undefined)
                          return
                        }
                        form.setValue('videoUrl', '')
                      }}
                      ref={field.ref}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="files"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Anexos</FormLabel>
                <FormControl>
                  <FileDropzone
                    value={field.value}
                    onChange={field.onChange}
                    onDeleteFile={handleDeleteAttachment}
                    isDeletingFile={isDeletingFile}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}
